import type {
  SearchRequest,
  SearchResult,
  SearchResultData,
  SearchType,
} from '../../action/search/searchTypes';
import {
  SearchResultType,
  SearchTypeEnum,
} from '../../action/search/searchTypes';
import { HTTP_STATUS_404, isErrorWithStatus } from '../../action/util/fetch';
import { TypeFilter } from '../../constants';
import type {
  RenderElement,
  RenderParam,
  RenderResultWithSsr,
} from '../../lib/renderTypes';
import { div, h1, p, span } from '../../render/html';
import {
  SUCHE__FEEDBACK__FEHLER,
  SUCHE__NICHT_GEFUNDEN,
  COMMON__HESSEN,
  SUCHERGEBNIS__ANSPRECHPARTNER_VORHANDEN,
  SUCHERGEBNIS__ONLINE_DIENST_VERFUEGBAR,
  SUCHERGEBNIS__ORTSANGABE_NOETIG,
  SUCHERGEBNIS__LEICHTE_SPRACHE_VERFUEGBAR,
  SUCHERGEBNIS__FUER_SUCHBEGRIFF,
  SUCHERGEBNIS__IN_REGION,
  SUCHERGEBNIS__TEXT__NICHT_GEFUNDEN,
  SUCHERGEBNIS__TEXT__EIN_TYP_EIN_ERGEBNIS,
  SUCHERGEBNIS__TEXT__MEHRERE_ERGEBNISSE,
  LIST,
  SUCHERGEBNIS__TITEL,
  SUCHERGEBNIS__AUSWERTUNGSFEHLER,
  SUCHERGEBNIS__ANZAHL_LEISTUNGEN,
  SUCHERGEBNIS__ANZAHL_BEHOERDEN,
  SUCHERGEBNIS__ANZAHL_INFORMATIONEN,
} from '../../texts';
import { UnreachableCaseError } from '../../util/UnreachableCaseError';
import { Screen } from '../../view';
import combinePlaceNames from '../util/combinePlaceNames';
import error from './error';
import { loadingIndicator } from './loading';
import './results.css';
import { askSearchWhere } from './results/askSearchWhere';
import { askDidYouMean } from './results/didYouMean';
import { resultListMulti } from './results/resultList';
import type { CountAndWord } from './results/resultTypes';
import { sectionSeiteninhalt } from './sectionSeiteninhalt';
import type { SimpleStore } from '../../state/SimpleStore';
import { Icons, renderIcon } from '../elements/icon';

export function results(
  simpleStore: SimpleStore,
  meta: RenderElement,
  showTitle: boolean,
  content: RenderElement[],
  loading: RenderResultWithSsr[],
  cssClass = '',
): RenderParam {
  const state = simpleStore.getState();
  const { data } = state.search.results;
  const searchResultLoading = Boolean(state.abortRequest.search);

  if (state.error.search) {
    return renderResultsError(state.error.search);
  }

  if (!data && !searchResultLoading) {
    return undefined;
  }

  return sectionSeiteninhalt(
    {
      ariaLabel: SUCHERGEBNIS__TITEL,
      class: `a-position-relative a-background-color-white ${cssClass}`,
    },
    [
      showTitle ? h1({ class: 'h3' }, SUCHERGEBNIS__TITEL) : undefined,
      div(
        {
          class: searchResultLoading
            ? 'og-results-transparent'
            : 'og-results-intransparent',
        },
        [meta, ...content],
      ),
      ...loading,
    ],
  );
}

export function renderResultsContent(
  simpleStore: SimpleStore,
  showScore: boolean,
  showTitle: boolean,
  shouldRenderAskList: boolean,
  orgAriaHint: string | undefined = undefined,
): (RenderResultWithSsr | undefined)[] {
  const state = simpleStore.getState();
  const { results: searchResults } = state.search;
  if (!searchResults.data || !searchResults.forRequest) {
    return [];
  } else {
    const { searchType } = searchResults.data;
    const { searchTerm } = searchResults.forRequest;
    const searchMoreResultLoading = Boolean(state.abortRequest.searchMore);
    const searchData =
      searchResults.data.type === SearchResultType.MESSAGE
        ? {
            type: searchResults.data.type,
            message: searchResults.data.message,
          }
        : {
            type: searchResults.data.type,
            result: searchResults.data.result,
            message: searchResults.data.message,
          };

    switch (searchType.type) {
      case SearchTypeEnum.HESSEN:
      case SearchTypeEnum.CITY: {
        const searchTermToShow =
          searchType.type === SearchTypeEnum.HESSEN
            ? searchTerm
            : searchType.internalSearchTerm;
        const result: (RenderResultWithSsr | undefined)[] = askDidYouMean({
          simpleStore,
          state,
        });
        return result
          .concat(
            askSearchWhere({
              simpleStore,
              state,
              shouldRenderAskList,
            }),
          )
          .concat(
            resultListMulti({
              simpleStore,
              showTitle,
              loadingMore: searchMoreResultLoading,
              orgAriaHint,
              searchResults: searchData,
              searchTerm: searchTermToShow,
              searchType,
              showPreviewOnly: searchResults.previewOnly,
              showScore,
            }),
          );
      }
      default:
        return [error(SUCHERGEBNIS__AUSWERTUNGSFEHLER)];
    }
  }
}

export function renderResultsError(searchError: unknown): RenderResultWithSsr {
  if (
    isErrorWithStatus(searchError) &&
    searchError.status === HTTP_STATUS_404
  ) {
    return error(SUCHE__NICHT_GEFUNDEN);
  } else {
    return error(SUCHE__FEEDBACK__FEHLER);
  }
}

export function renderResultsLoading(
  dataDisplayed: boolean,
): RenderResultWithSsr[] {
  return [
    div(
      {
        class: `og-search-results-loading ${
          dataDisplayed ? 'og-search-results-loading-overlay' : ''
        }`,
      },
      loadingIndicator(),
    ),
  ];
}

export function buildCountInfoText({
  suchbegriffText,
  regionText,
  entries,
}: {
  suchbegriffText: string | undefined;
  regionText: string | undefined;
  entries: CountAndWord[];
}): string {
  const parts = entries.map((entry) => entry.localizedText);
  const forPart = suchbegriffText
    ? SUCHERGEBNIS__FUER_SUCHBEGRIFF(suchbegriffText)
    : '';
  const inPart = regionText ? SUCHERGEBNIS__IN_REGION(regionText) : '';
  if (parts.length === 0) {
    return SUCHERGEBNIS__TEXT__NICHT_GEFUNDEN(forPart, inPart);
  } else if (parts.length === 1) {
    if (entries[0].count === 1) {
      return SUCHERGEBNIS__TEXT__EIN_TYP_EIN_ERGEBNIS(
        forPart,
        inPart,
        parts[0],
      );
    } else {
      return SUCHERGEBNIS__TEXT__MEHRERE_ERGEBNISSE(forPart, inPart, parts[0]);
    }
  } else {
    return SUCHERGEBNIS__TEXT__MEHRERE_ERGEBNISSE(forPart, inPart, LIST(parts));
  }
}

export function foundText(
  simpleStore: SimpleStore,
  result: SearchResult,
  forRequest: SearchRequest,
) {
  if (result.type === SearchResultType.MESSAGE) {
    return undefined;
  }

  const { searchType } = result;
  const { searchTerm } = forRequest;
  switch (searchType.type) {
    case SearchTypeEnum.CITY:
      return foundTextResult(
        simpleStore,
        result.result,
        searchType,
        searchType.internalSearchTerm,
      );
    case SearchTypeEnum.HESSEN:
      return foundTextResult(
        simpleStore,
        result.result,
        searchType,
        searchTerm,
      );
    default:
      return undefined;
  }
}

function foundTextResult(
  simpleStore: SimpleStore,
  searchResultData: SearchResultData,
  searchType: SearchType,
  searchTerm: string,
) {
  const countEditorialPages = searchResultData.count.editorialPages || 0;
  const countLeistungen = searchResultData.count.leistungen || 0;
  const countOrgeinheiten = searchResultData.count.orgeinheiten || 0;

  let placeText: string | undefined;
  switch (searchType.type) {
    case SearchTypeEnum.HESSEN:
      placeText = COMMON__HESSEN;
      break;
    case SearchTypeEnum.CITY:
      placeText = combinePlaceNames(searchType.city);
      break;
    default:
      placeText = undefined;
      break;
  }

  let types: TypeFilter[];
  const state = simpleStore.getState();
  switch (state.screen) {
    case Screen.Bereich:
    case Screen.Lage:
    case Screen.Sublage:
    case Screen.Suche:
      types = simpleStore.getState().search.request.filter.type;
      break;
    case Screen.OrgSuche:
      types = [TypeFilter.orgeinheit];
      break;
    case Screen.Leistung:
    case Screen.Org:
    case Screen.Startseite:
    case Screen.Information:
      // Komponente wird nicht verwendet
      types = [];
      break;
    default:
      throw new UnreachableCaseError(state.screen);
  }

  const countAndWords: CountAndWord[] = [];
  if (types.length === 0 || types.includes(TypeFilter.leistung)) {
    countAndWords.push({
      count: countLeistungen,
      localizedText: SUCHERGEBNIS__ANZAHL_LEISTUNGEN(countLeistungen),
    });
  }
  if (types.length === 0 || types.includes(TypeFilter.info)) {
    countAndWords.push({
      count: countEditorialPages,
      localizedText: SUCHERGEBNIS__ANZAHL_INFORMATIONEN(countEditorialPages),
    });
  }
  if (types.length === 0 || types.includes(TypeFilter.orgeinheit)) {
    countAndWords.push({
      count: countOrgeinheiten,
      localizedText: SUCHERGEBNIS__ANZAHL_BEHOERDEN(countOrgeinheiten),
    });
  }

  return p(
    { class: 'paragraph bg-light p-3 font-font-roc-grotesk-w05-regular mb-4' },
    buildCountInfoText({
      suchbegriffText: searchTerm,
      regionText: placeText,
      entries: countAndWords,
    }),
  );
}

export function legend(icons: Icons) {
  return div(
    {
      class:
        'bg-light d-flex flex-wrap column-gap-5 p-3 mb-4 font-font-roc-grotesk-w05-regular font-size-4',
      'aria-hidden': true,
    },
    [
      div({}, [
        div(
          { class: 'd-flex' },
          renderIcon(icons, {
            iconId: 'display',
            class: 'og-type-icon',
            alt: SUCHERGEBNIS__ONLINE_DIENST_VERFUEGBAR,
          }),
          span({ class: '' }, SUCHERGEBNIS__ONLINE_DIENST_VERFUEGBAR),
        ),
        div(
          { class: 'd-flex' },
          renderIcon(icons, {
            iconId: 'buildings',
            class: 'og-type-icon',
            alt: SUCHERGEBNIS__ANSPRECHPARTNER_VORHANDEN,
          }),
          span(
            { class: 'font-font-roc-grotesk-w05-regular' },
            SUCHERGEBNIS__ANSPRECHPARTNER_VORHANDEN,
          ),
        ),
      ]),
      div({}, [
        div(
          { class: 'd-flex' },
          renderIcon(icons, {
            iconId: 'map',
            class: 'og-type-icon',
            alt: SUCHERGEBNIS__ORTSANGABE_NOETIG,
          }),
          span(
            { class: 'font-font-roc-grotesk-w05-regular' },
            SUCHERGEBNIS__ORTSANGABE_NOETIG,
          ),
        ),
        div(
          { class: 'd-flex' },
          renderIcon(icons, {
            iconId: 'leichte_sprache',
            class: 'og-type-icon',
            alt: SUCHERGEBNIS__LEICHTE_SPRACHE_VERFUEGBAR,
          }),
          span(
            { class: 'font-font-roc-grotesk-w05-regular' },
            SUCHERGEBNIS__LEICHTE_SPRACHE_VERFUEGBAR,
          ),
        ),
      ]),
    ],
  );
}
